import React, { useContext, useState } from 'react';

import classnames from 'classnames';
import { BarChart, FilePlus2, Trash, UserX } from 'lucide-react';

import {
  type QueryParams,
  invalidateQueriesAndReload,
  loadMore,
} from 'common/actions/inboxItemQueries';
import AJAX from 'common/AJAX';
import Tooltip from 'common/common/Tooltip';
import { LocationContext, RouterContext } from 'common/containers/RouterContainer';
import { ShowToastContext, ToastTypes } from 'common/containers/ToastContainer';
import connect from 'common/core/connect';
import { KeyIdentifiers } from 'common/KeyCodes';
import LazyLoadedImage from 'common/LazyLoadedImage';
import ModernConfirmModal from 'common/modals/ModernConfirmModal';
import SelectableDataTable from 'common/SelectableDataTable';
import IconButtonV2 from 'common/ui/IconButtonV2';
import { P } from 'common/ui/Text';
import parseAPIResponse, { isDefaultSuccessResponse } from 'common/util/parseAPIResponse';

import AdminQueueNUX from './AdminQueueNUX';
import AdminQueuePost from './AdminQueuePost';

import SpamBoxChartIcon from 'img/icons/spambox-chart.svg';

import 'css/components/subdomain/admin/_AdminQueuePage.scss';

import type { InboxComment } from 'common/api/resources/comments';
import type { InboxPost } from 'common/api/resources/posts';
import type { Column, Sort } from 'common/DataTable';
import type { Dispatch } from 'redux-connect';

type InboxItem = InboxPost | InboxComment;

enum InboxType {
  comment = 'comment',
  post = 'post',
}

type Row = {
  id: string;
  score: number;
  type: InboxType;
  item: InboxPost | InboxComment;
  actions: {
    approve: () => Promise<void>;
    reject: () => Promise<void>;
    delete: () => Promise<void>;
  };
};

type SortableColumn<R extends Row, ID extends keyof R> = Column<R, ID> & {
  sort: (order: 'asc' | 'desc') => void;
};

type ItemList = {
  items: InboxItem[];
  loading: boolean;
  loadingMore: boolean;
  error?: string;
  lastCreated?: string;
  queryParams: QueryParams;
};

type Props = {
  inboxItemList: ItemList;
  invalidateQueriesAndReload: (queryParams: QueryParams) => void;
  loadMore: (itemList: ItemList) => void;
};

type ActionsCellProps = {
  row: Row;
};

enum Modal {
  reject = 'reject',
}

enum Action {
  approve = 'approve',
  reject = 'reject',
  delete = 'delete',
}

const ActionsCell = ({ row }: ActionsCellProps) => {
  const [modal, setModal] = useState<Modal | null>(null);
  const [loadingAction, setLoadingAction] = useState<Action | null>();

  const openRejectModal = () => {
    setModal(Modal.reject);
  };

  const performApprove = async () => {
    setLoadingAction(Action.approve);
    await row.actions.approve();
    setLoadingAction(null);
  };

  const performReject = async () => {
    setLoadingAction(Action.reject);
    await row.actions.reject();
    setLoadingAction(null);
  };

  const performDelete = async () => {
    setLoadingAction(Action.delete);
    await row.actions.delete();
    setLoadingAction(null);
  };

  return (
    <div className="queueActions">
      <Tooltip delay={1000} value="Approve content" position="left">
        <IconButtonV2
          icon={FilePlus2}
          aria-label="approve content"
          size="medium"
          variant="plain"
          disabled={loadingAction === Action.approve}
          loading={loadingAction === Action.approve}
          onClick={performApprove}
          className="approve"
        />
      </Tooltip>
      <div className="verticalSpacer"></div>
      <Tooltip delay={1000} value="Confirm spam" position="left">
        <IconButtonV2
          icon={UserX}
          color="error"
          aria-label="confirm spam"
          disabled={loadingAction === Action.reject}
          loading={loadingAction === Action.reject}
          size="medium"
          variant="plain"
          onClick={openRejectModal}
          className="reject"
        />
      </Tooltip>
      <Tooltip delay={1000} value="Delete post" position="left">
        <IconButtonV2
          icon={Trash}
          color="error"
          aria-label="delete post"
          disabled={loadingAction === Action.delete}
          loading={loadingAction === Action.delete}
          size="medium"
          variant="plain"
          onClick={performDelete}
          className="delete"
        />
      </Tooltip>
      {modal === Modal.reject && (
        <ModernConfirmModal
          onClose={() => setModal(null)}
          onConfirm={performReject}
          confirmText="Ban and delete user"
          type="destructive"
          header="Are you sure you want to ban and delete this user?">
          <P>The following action will:</P>
          <ul>
            <li>Delete all of their posts, comments, and votes.</li>
            <li>Ban them from voting, along with creating future posts and comments.</li>
          </ul>
          <P>This action cannot be&nbsp;reversed.</P>
        </ModernConfirmModal>
      )}
    </div>
  );
};

const AdminQueueSpam = ({ inboxItemList, invalidateQueriesAndReload, loadMore }: Props) => {
  const showToast = useContext(ShowToastContext);
  const location = useContext(LocationContext);
  const router = useContext(RouterContext);

  const columns = [
    {
      id: 'score',
      align: 'left',
      header: 'Confidence',
      sortable: false,
      cell: (score) => {
        if (score >= 5) {
          return (
            <div className={classnames('spamFlag', 'high')}>
              <BarChart size={14} />
              <P className="label">High</P>
            </div>
          );
        }

        return (
          <div className={classnames('spamFlag', 'low')}>
            <LazyLoadedImage src={SpamBoxChartIcon} width={14} alt="Spambox chart icon" />
            <P className="label">Low</P>
          </div>
        );
      },
    } as Column<Row, 'score'>,
    {
      id: 'type',
      align: 'left',
      sortable: false,
      header: 'Type',
      cell: (type) => <P className="spamType">{type}</P>,
    } as Column<Row, 'type'>,
    {
      id: 'item',
      align: 'left',
      header: 'Details',
      sortable: true,
      fullWidth: true,
      sort: (order) => {
        router.push({
          pathname: location.pathname,
          query: { ...location.query, sort: order === 'asc' ? 'oldest' : 'newest' },
        });
      },
      cell: (item) => {
        if (item.itemType === InboxType.post) {
          return (
            <AdminQueuePost
              className="queueItem spam"
              title={item.title}
              details={item.details}
              author={item.author}
              created={item.created}
              board={item.board}
            />
          );
        } else if (item.itemType === InboxType.comment) {
          return (
            <AdminQueuePost
              className="queueItem spam"
              title={item.post.title}
              details={item.value}
              author={item.author}
              created={item.created}
              board={item.post.board}
            />
          );
        }

        return null;
      },
    } as SortableColumn<Row, 'item'>,
    {
      id: 'actions',
      align: 'left',
      header: 'Quick actions',
      cell: (_, row) => {
        return <ActionsCell row={row} />;
      },
    } as Column<Row, 'actions'>,
  ];

  const itemToRow = (item: InboxItem) => {
    // actions
    const approve = async () => {
      const response = await AJAX.post('/api/spam/unmark', {
        objectID: item._id,
        objectType: item.itemType,
      });

      const { error } = parseAPIResponse(response, {
        isSuccessful: isDefaultSuccessResponse,
        errors: {
          'not authorized': `You don't have the permissions take action on this item`,
          'invalid commentID': 'Comment is invalid',
          'invalid postID': 'Post is invalid',
        },
      });

      if (error) {
        showToast(error.message, ToastTypes.error);
        return;
      }

      await invalidateQueriesAndReload(inboxItemList.queryParams);
    };

    const deletePost = async () => {
      const response = await AJAX.post('/api/posts/delete', {
        postID: item._id,
      });

      const { error } = parseAPIResponse(response, {
        isSuccessful: isDefaultSuccessResponse,
        errors: {
          'not authorized': `You don't have the permissions take action on this item`,
        },
      });

      if (error) {
        showToast(error.message, ToastTypes.error);
        return;
      }

      await invalidateQueriesAndReload(inboxItemList.queryParams);
    };

    const deleteComment = async () => {
      const response = await AJAX.post('/api/comments/delete', {
        commentID: item._id,
      });

      const { error } = parseAPIResponse(response, {
        isSuccessful: isDefaultSuccessResponse,
        errors: {
          'not authorized': `You don't have the permissions take action on this item`,
        },
      });

      if (error) {
        showToast(error.message, ToastTypes.error);
        return;
      }

      await invalidateQueriesAndReload(inboxItemList.queryParams);
    };

    const reject = async () => {
      const keyMap: Record<Row['type'], string> = {
        comment: 'commentID',
        post: 'postID',
      };

      const response = await AJAX.post('/api/spam/mark', {
        [keyMap[item.itemType]]: item._id,
      });

      const { error } = parseAPIResponse(response, {
        isSuccessful: isDefaultSuccessResponse,
        errors: {
          'not authorized': `You don't have the permissions take action on this item`,
        },
      });

      if (error) {
        showToast(error.message, ToastTypes.error);
        return;
      }
    };

    return {
      id: item._id,
      score: item.spamScore,
      type: item.itemType as InboxType,
      item,
      actions: {
        approve,
        reject,
        delete: item.itemType === InboxType.comment ? deleteComment : deletePost,
      },
    };
  };

  const isReady = !inboxItemList.error && !inboxItemList.loading;
  const rows = isReady ? inboxItemList.items.map<Row>(itemToRow) : [];

  if (isReady && !rows.length) {
    return (
      <div className="adminQueuePage empty">
        <AdminQueueNUX
          title="No spam detected!"
          info={[
            'Canny automatically identifies spam so you can easily moderate your feedback.',
            'Check back regularly to take action on any detected spam.',
          ]}
        />
      </div>
    );
  }

  const onSort = ({ columnID, order }: Sort) => {
    const column = columns.find((column) => columnID === column.id);
    if (column && 'sort' in column) {
      column.sort(order);
    }
  };

  return (
    <div className="adminQueuePage">
      <SelectableDataTable<Row>
        cellAlignment="top"
        loading={inboxItemList.loading}
        padding="medium"
        error={inboxItemList.error}
        rounded={false}
        onSort={onSort}
        defaultSort={
          location.sort
            ? {
                columnID: 'details',
                order: location.sort === 'oldest' ? 'asc' : 'desc',
              }
            : undefined
        }
        hotkeys={{
          [KeyIdentifiers.N]: (_, element: HTMLElement | null) => {
            const button: HTMLElement | null =
              element?.querySelector('.queueActions > .approve') ?? null;
            button?.click();
          },
          [KeyIdentifiers.X]: (_, element: HTMLElement | null) => {
            const button: HTMLElement | null =
              element?.querySelector('.queueActions > .delete') ?? null;
            button?.click();
          },
          [KeyIdentifiers.B]: (_, element: HTMLElement | null) => {
            const button: HTMLElement | null =
              element?.querySelector('.queueActions > .reject') ?? null;
            button?.click();
          },
        }}
        pagination={{
          loading: inboxItemList.loadingMore,
          hasMore: !!(inboxItemList.items && inboxItemList.lastCreated),
          onLoadMore: () => loadMore(inboxItemList),
        }}
        columns={columns}
        rows={rows}
      />
    </div>
  );
};

export default connect(null, (dispatch: Dispatch) => ({
  invalidateQueriesAndReload: (queryParams: QueryParams) =>
    dispatch(invalidateQueriesAndReload(queryParams)),
  loadMore: (itemList: ItemList) => dispatch(loadMore(itemList)),
}))(AdminQueueSpam);
